import React, { useState, useEffect } from 'react'
import api from '../api/client'
import type { Shift, VolunteerMetadata } from '../types'
import { X, Search, UserPlus, Mail, Phone } from 'lucide-react'
import { StatusBadge } from '@rallyshift/design-system'

interface AssignVolunteerModalProps {
  isOpen: boolean
  onClose: () => void
  shift: Shift | null
  onVolunteerAssigned: () => void
}

const AssignVolunteerModal: React.FC<AssignVolunteerModalProps> = ({
  isOpen,
  onClose,
  shift,
  onVolunteerAssigned
}) => {
  const [volunteers, setVolunteers] = useState<VolunteerMetadata[]>([])
  const [filteredVolunteers, setFilteredVolunteers] = useState<VolunteerMetadata[]>([])
  const [loading, setLoading] = useState(false)
  const [assigning, setAssigning] = useState(false)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [assignedVolunteerIds, setAssignedVolunteerIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (isOpen && shift) {
      fetchVolunteers()
      fetchAssignedVolunteers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, shift])

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredVolunteers(volunteers)
    } else {
      const filtered = volunteers.filter(volunteer =>
        volunteer.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        volunteer.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        volunteer.email.toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredVolunteers(filtered)
    }
  }, [volunteers, searchTerm])

  const fetchVolunteers = async () => {
    try {
      setLoading(true)
      const data = await api.volunteerMetadata.list()
      setVolunteers(data || [])
    } catch (err: any) {
      setError(err.message || 'Failed to fetch volunteers')
    } finally {
      setLoading(false)
    }
  }

  const fetchAssignedVolunteers = async () => {
    if (!shift) return
    try {
      const data = await api.shifts.getAssignments(shift.id)
      const assignedIds = new Set((data || []).filter((a: any) => ['assigned', 'confirmed', 'completed'].includes(a.status)).map((a: any) => a.volunteer_id))
      setAssignedVolunteerIds(assignedIds)
    } catch (err: any) {
      console.error('Error fetching assigned volunteers:', err)
    }
  }

  const handleAssignVolunteer = async (volunteerId: string) => {
    if (!shift) return

    try {
      setAssigning(true)
      setError('')
      await api.shifts.upsertAssignment(shift.id, volunteerId, 'assigned')
      setAssignedVolunteerIds(prev => new Set(prev).add(volunteerId))
      onVolunteerAssigned()
      onClose()
    } catch (err: any) {
      console.error('Error assigning volunteer:', err)
      setError(err.message || 'Failed to assign volunteer')
    } finally {
      setAssigning(false)
    }
  }

  if (!isOpen || !shift) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Assign Volunteer to: {shift.title}
              </h3>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}

            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search volunteers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Volunteers List */}
            <div className="max-h-96 overflow-y-auto">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500">Loading volunteers...</p>
                </div>
              ) : filteredVolunteers.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500">No volunteers found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredVolunteers.map((volunteer) => {
                    const isAssigned = assignedVolunteerIds.has(volunteer.id)
                    const isDisabled = isAssigned || assigning

                    return (
                      <div
                        key={volunteer.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          isAssigned 
                            ? 'bg-gray-50 border-gray-200' 
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          {volunteer.avatar_url ? (
                            <img
                              className="h-10 w-10 rounded-full"
                              src={volunteer.avatar_url}
                              alt={`${volunteer.first_name} ${volunteer.last_name}`}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-700">
                                {volunteer.first_name[0]}{volunteer.last_name[0]}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {volunteer.first_name} {volunteer.last_name}
                            </p>
                            <div className="flex items-center space-x-4 mt-1">
                              <div className="flex items-center text-xs text-gray-500">
                                <Mail className="h-3 w-3 mr-1" />
                                {volunteer.email}
                              </div>
                              {volunteer.phone_number && (
                                <div className="flex items-center text-xs text-gray-500">
                                  <Phone className="h-3 w-3 mr-1" />
                                  {volunteer.phone_number}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {isAssigned && (
                            <StatusBadge
                              dimension="assignment"
                              status="assigned"
                              size="sm"
                              label="Al ingedeeld"
                            />
                          )}
                          <button
                            onClick={() => handleAssignVolunteer(volunteer.id)}
                            disabled={isDisabled}
                            className={`flex items-center space-x-1 px-3 py-1 rounded-lg text-sm font-medium ${
                              isDisabled
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                          >
                            <UserPlus className="h-4 w-4" />
                            <span>{isAssigned ? 'Assigned' : 'Assign'}</span>
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AssignVolunteerModal
